/**
 * SalesInfoPanel — Tier 0 slice-local property sales-info surface (SCRUM-337).
 *
 * The create-reservation form shows the selected property's identity (name)
 * and occupancy capacity (sleeps / bedrooms / bathrooms) so a phone-in agent
 * confirms they picked the right unit. Two responsive shapes drive the same
 * content:
 *
 *   - **Desktop (`compact={false}`)** — a persistent `<aside>` alongside the
 *     form. Always visible; `open`/`onClose` are ignored (no dismissal).
 *   - **Mobile (`compact={true}`)** — a slide-out `role="dialog"` panel over a
 *     dimmed backdrop, shown only when `open`. Esc closes it (`onClose`),
 *     a backdrop click closes it, and focus is trapped inside via the shared
 *     `useFocusTrap` hook (WAI-ARIA §3.9). When `open={false}` it renders
 *     nothing (the trigger + form remain fully interactive).
 *
 * **Scope (plan).** Renders ONLY name + maxSleeps + bedrooms + bathrooms.
 * Amenities, ocean-view, and the maintenance warning are DEFERRED (BE-ready
 * no-FE-hook / no wire field) — never fabricated here.
 *
 * The panel is PRESENTATIONAL: the form owns the `usePropertySalesInfo` call
 * and the responsive `compact`/`open` state and spreads `{ name, capacity }`
 * down. No hook, no data source of its own.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** Semantic `var(--token)`s only —
 * no hex, no inline pixel literals. **A11y (WCAG 2.1 AA).** Semantic HTML,
 * dialog role + label on the slide-out, Esc + focus-trap, counts carried by
 * TEXT (never color alone); axe-clean in light + dark.
 */

import * as React from 'react';
import { Badge, Icon, IconBtn } from '@sparta/ui-kit';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';

export interface SalesInfoPanelCapacity {
  readonly maxSleeps: number;
  readonly maxAdults: number;
  readonly bedrooms: number;
  readonly bathrooms: number;
}

export interface SalesInfoPanelProps {
  /** The selected property's display name. */
  readonly name: string;
  /** Occupancy capacity — only sleeps/bedrooms/bathrooms are rendered. */
  readonly capacity: SalesInfoPanelCapacity;
  /** `false` = persistent desktop aside; `true` = mobile slide-out. */
  readonly compact: boolean;
  /** Mobile slide-out open/closed state (ignored when `compact={false}`). */
  readonly open: boolean;
  /** Called on Esc / backdrop-click to dismiss the mobile slide-out. */
  readonly onClose: () => void;
}

/* ── shared content: name + capacity counts ──────────────────────────────── */

function SalesInfoContent({
  name,
  capacity,
  headingId,
}: {
  readonly name: string;
  readonly capacity: SalesInfoPanelCapacity;
  readonly headingId: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-1 text-[color:var(--fg-tertiary)]">
          <Icon name="building" size={20} />
        </span>
        <h2 id={headingId} className="m-0 text-base font-semibold text-[color:var(--fg-primary)]">
          {name}
        </h2>
      </div>

      <dl className="m-0 flex flex-col gap-2">
        <CapacityRow label="Sleeps" value={capacity.maxSleeps} unit="guests" />
        <CapacityRow label="Bedrooms" value={capacity.bedrooms} unit="bed" />
        <CapacityRow label="Bathrooms" value={capacity.bathrooms} unit="bath" />
      </dl>
    </div>
  );
}

/**
 * One capacity row — the count is carried by TEXT (`3 bed`) so it never relies
 * on color, and the unit word makes the number self-describing for a screen
 * reader (WCAG 1.4.1 / 1.3.1).
 */
function CapacityRow({
  label,
  value,
  unit,
}: {
  readonly label: string;
  readonly value: number;
  readonly unit: string;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-sm text-[color:var(--fg-secondary)]">{label}</dt>
      <dd className="m-0">
        <Badge variant="neutral">
          {value} {unit}
        </Badge>
      </dd>
    </div>
  );
}

export function SalesInfoPanel({
  name,
  capacity,
  compact,
  open,
  onClose,
}: SalesInfoPanelProps): React.ReactElement | null {
  const headingId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  // Focus trap only engages for the OPEN mobile slide-out (the desktop aside
  // is not a modal). The hook no-ops when `enabled` is false.
  const trapEnabled = compact && open;
  useFocusTrap(dialogRef, { enabled: trapEnabled });

  // Esc closes the OPEN mobile slide-out. A document-level listener (mirrors
  // NewImportSheet) so an Esc press anywhere while the modal is up dismisses
  // it; the hook only owns focus, the caller owns close (per useFocusTrap docs).
  React.useEffect(() => {
    if (!trapEnabled) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [trapEnabled, onClose]);

  // ── desktop: persistent aside ────────────────────────────────────────────
  if (!compact) {
    return (
      <aside
        aria-labelledby={headingId}
        data-testid="sales-info-aside"
        className="rounded-2xl border border-solid border-[var(--border-subtle)] bg-[var(--bg-surface)] p-5"
      >
        <SalesInfoContent name={name} capacity={capacity} headingId={headingId} />
      </aside>
    );
  }

  // ── mobile: closed slide-out renders nothing ─────────────────────────────
  if (!open) {
    return null;
  }

  // ── mobile: open slide-out over an inert, dismissible backdrop ────────────
  //
  // The backdrop and the dialog are SIBLINGS inside a layout-only positioning
  // container (no role / no aria on the wrapper). The backdrop is a full-screen
  // scrim BEHIND the dialog: it carries `aria-hidden="true"` + `tabIndex={-1}`
  // so assistive technology skips it entirely (WCAG 4.1.2 — a bare click
  // surface has no accessible name/role to expose). Its `onClick` is a
  // redundant MOUSE convenience — the keyboard/AT close path is the in-dialog
  // Close button + Esc, both of which remain. Because the dialog is a sibling
  // painted ON TOP (not a child of the backdrop), clicking the dialog does not
  // hit the backdrop and does NOT dismiss.
  return (
    <div className="fixed inset-0 z-50">
      <div
        data-testid="backdrop"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: 'color-mix(in oklab, var(--fg-primary) 40%, transparent)' }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="sales-info-slideout"
        className="absolute inset-y-0 right-0 flex h-full w-full max-w-sm flex-col gap-4 overflow-y-auto bg-[var(--bg-surface)] p-5 shadow-[var(--shadow-xl)]"
      >
        <div className="flex items-center justify-end">
          <IconBtn label="Close sales info" onClick={onClose}>
            <Icon name="close" size={20} />
          </IconBtn>
        </div>
        <SalesInfoContent name={name} capacity={capacity} headingId={headingId} />
      </div>
    </div>
  );
}
